import React, { FC } from 'react';
import cn from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { PostDrugResponse } from '../../../api/requests/postDrugSearch';
import AccordionDefaultBody from '../../../shared-components/Accordion/AccordionDefaultBody';
import {
  AccordionHeaderContent,
  AccordionItemContentType
} from '../../../api/types';
import AccordionDefaultHeader from '../../../shared-components/Accordion/AccordionDefaultHeader';
import Accordion from '../../../shared-components/Accordion/Accordion';
import trns from '../../drug/trns';
import { PROD_DOMAIN } from '../../../api/constants';
import titleTrns from '../../help/trns';
import useGTAG from '../../../hooks/useGTAG';

interface Props {
  finalEntityName: string;
  popularDrugs: PostDrugResponse;
  cheapDrugs: PostDrugResponse;
}

const FAQ: FC<Props> = ({ finalEntityName, popularDrugs, cheapDrugs }) => {
  const intl = useIntl();
  const listGroups = [
    {
      id: '1',
      title: `${intl.formatMessage(
        trns.good
      )} ${finalEntityName} ${intl.formatMessage(trns.homeDelivery)}`,
      description: ''
    },
    {
      id: '2',
      title: `${intl.formatMessage(
        trns.isCheap
      )} ${finalEntityName} ${intl.formatMessage(trns.isInPharmacy)}`,
      description: ''
    },
    {
      id: '3',
      title: `${intl.formatMessage(
        trns.canBuy
      )} ${finalEntityName} ${intl.formatMessage(trns.withDiscount)}?`,
      description: ''
    },
    {
      id: '4',
      title: `${intl.formatMessage(
        trns.canOrder
      )} ${finalEntityName} ${intl.formatMessage(trns.canOrderOnline)}?`,
      description: ''
    }
  ];
  const listContent = {
    1: (
      <div>
        {`${intl.formatMessage(trns.oftenBuy)}:`}
        <ul>
          {popularDrugs?.drugs.slice(0, 3).map((drug) => {
            return (
              <li key={drug.translitName}>
                <a href={`${PROD_DOMAIN}/product/${drug?.translitName}`}>
                  {drug?.name}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    ),
    2: (
      <div>
        {`${intl.formatMessage(
          trns.isCheapGoods
        )} ${finalEntityName}, ${intl.formatMessage(trns.topCheapGoods, {
          topGoods: intl.formatMessage(trns.topGoods)
        })}:`}
        <ul>
          {cheapDrugs?.drugs.slice(0, 3).map((drug) => {
            return (
              <li key={drug.translitName}>
                <a href={`${PROD_DOMAIN}/product/${drug?.translitName}`}>
                  {drug?.name}
                </a>{' '}
                {intl.formatMessage(trns.priceFrom)}{' '}
                {drug?.minPrice?.toFixed(2) || '0.00'} ₴
              </li>
            );
          })}
        </ul>
      </div>
    ),
    3: (
      <div>
        {`${intl.formatMessage(trns.topGoods)}:`}
        <ul>
          {cheapDrugs?.drugs.slice(0, 3).map((drug) => {
            return (
              <li key={drug?.translitName}>
                <a href={`${PROD_DOMAIN}/product/${drug?.translitName}`}>
                  {drug?.name}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    ),
    4: (
      <div>
        {`${intl.formatMessage(
          trns.yesCanBuy
        )} ${finalEntityName} ${intl.formatMessage(trns.paymentMethods)}`}{' '}
        <a href={`${PROD_DOMAIN}/privacy_policy`}>
          {' '}
          {intl.formatMessage(trns.paymentPart)}.
        </a>
      </div>
    )
  };
  const { trackGtagEvent } = useGTAG();
  return (
    <div className="px-4 lm:px-0">
      <p className="text-[32px] uppercase text-astronautBlue leading-9 fs-body-bold pb-6 pt-7">
        {' '}
        {<FormattedMessage {...titleTrns.answers} />}{' '}
      </p>
      <Accordion
        accordionList={listGroups.map((group) => ({
          header: group,
          body: [
            <div
              key={group.id}
              className={cn(
                'fs-caption-reg text-mako compendium-innerHtmlStyling bg-white rounded-b-xl',
                'xs:w-1/2 sm:w-2/3 xs:rounded-b-0 xs:pb-0 xs:pr-0'
              )}
            >
              {listContent[group.id]}
            </div>
          ]
        }))}
        renderItem={(content) => <AccordionDefaultBody content={content} />}
        classNameButton={'pt-3.5 pb-3.5 border-b border-periwinkleGray'}
        onToggleHandler={(title) => {
          trackGtagEvent('subcategory_questions', title, 'title');
        }}
        renderHeader={(
          header: AccordionHeaderContent,
          productsList: AccordionItemContentType[],
          active: false
        ) => (
          <AccordionDefaultHeader
            header={header}
            productsList={productsList}
            active={active}
          />
        )}
      />
    </div>
  );
};

export default FAQ;
